import { createDebouncedDerivedAtom } from '@/lib/atom-utils';
import { GpuFarmName } from '@/lib/gpu/GpuFarm';
import { log } from '@logtail/next';
import { detectRequiredAudioFields } from 'app/animate/[[...id]]/animate-utils-client-safe';
import { AspectRatio, Preset } from 'app/animate/[[...id]]/CreateAnimationTypes';
import { AudioAnalysisData, AudioBeat, AudioEvent } from 'app/api/audioInput/lib';
import { DeforumStudioBackend } from 'app/types';
import { aspectRatios } from 'data/aspect-ratios';
import { atom } from 'jotai';
import JSON5 from 'json5';
import { DeforumSettings } from 'models/jobRequest';
import { AudioPreProcessingConfig } from '../PresetLab';


export const promptAtom = atom<string>('');
export const styleAtom = atom<string>('(Random)');
export const classicMotionPresetAtom = atom<string>('(Random)');
export const audioSyncMotionPresetAtom = atom<string>('(Random)');
export const aspectRatioAtom = atom<AspectRatio>(aspectRatios[0]);

// Classic-mode only form state
export const slowMoAtom = atom(false);
export const duration30sAtom = atom(false);

// ATK-only form state
//export const aDiffPassAtom = atom(false);
export const smoothingAtom = atom<number>(0);

// Image prompt form state
export const useImagePromptAtom = atom(false);
export const imagePromptAtom = atom<string|undefined>(undefined);
export const imagePromptWeightAtom = atom<string>("0.6");
export const imagePromptNsfwScoreAtom = atom<string | undefined>(undefined);
export const imagePromptThumbnailAtom = atom<string | undefined>(undefined);

// Audio-related form state
export const animationTypeAtom = atom<'classic' | 'audio-reactive'>('classic');
export const audioLocalFilenameAtom = atom<string | undefined>(undefined);
export const audioUrlAtom = atom<string | undefined>(undefined);
export const audioSegmentStartAtom = atom<number|undefined>(undefined);
export const audioSegmentEndAtom = atom<number|undefined>(undefined);
export const audioSegmentAnalysisAtom = atom<AudioAnalysisData|undefined>(undefined);
export const audioEventDataAtom = atom<AudioEvent[]|undefined>(undefined);
export const audioBeatDataAtom = atom<AudioBeat|undefined>(undefined);
export const audioFilenameAtom = atom<string|undefined>(undefined);

// Admin overrides state
export const settingsOverridesAtom = atom<string|undefined>(undefined);
export const gpuFarmOverrideAtom = atom<GpuFarmName | undefined>(undefined);
export const backendOverrideAtom = atom<DeforumStudioBackend | undefined>(undefined);
export const audioPresetDataAtom = atom<string | undefined>(undefined);

// Transient state – this is state shared by components but not persisted to the DB 
export const isEstimatingTempoAtom = atom<boolean>(false);
export const audioTotalDurationAtom = atom<number>(0);
export const submittingNewAnimationAtom = atom<boolean>(false);
export const librosaEventsAtom = atom<{t:number, strength:number}[]>([]);
export const isMobileAudioEditorOpenAtom = atom(false);

//export const audioBufferAtom = atom<AudioBuffer|undefined>(undefined);

// Experimental settings for advanced audio control
export const audioProcessingConfigsAtom = atom<AudioPreProcessingConfig[]>([]);
export const framewiseDataSeriesAtom = atom<Record<string, number[]>>({});


// Configuration passed from server to multiple client components
export const allPresetsAtom = atom<Preset[]>([]); // Initialised client-side by PresetsInitializer


// Derived state ------------------------------------------------------------------------------------------------------------------------------

// TODO: come back to this. We will use this to reduce number of URL <-> AudioBuffer conversions
// export const audioBufferAtom = atom(async (get) =>{
//     log.debug("Deriving audio buffer from URL.")
//     const audioUrl = get(audioUrlAtom);
//     if (audioUrl) {
//         return await dataUrlToAudioBuffer(audioUrl);
//     }
//     return undefined;
// });

// Maintain a derived atom that parses the settings override string into a JSON object
export const parsedSettingsOverridesAtom = createDebouncedDerivedAtom([settingsOverridesAtom], (settingsOverride: string|undefined) => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return settingsOverride ? (JSON5.parse(settingsOverride) as Partial<DeforumSettings>)  : undefined;
    } catch (error) {
        return undefined;
    }
}, 200);


// Maintain a derived atom that parses the audio preset data string into a JSON object
export const activePresetInfoAtom = atom((get) => {
    const isAudioReactive = get(animationTypeAtom) === 'audio-reactive';
    const activePreset = isAudioReactive ? get(audioSyncMotionPresetAtom) : get(classicMotionPresetAtom);
    const presets = get(allPresetsAtom);
    return presets.find(p => p.displayName === activePreset);

})

// Maintain a derived atom that calculates the FPS based on the active preset and settings overrides
export const fpsAtom = atom((get) => {
    
    const activePreset = get(activePresetInfoAtom);
    const parsedSettingsOverrides = get(parsedSettingsOverridesAtom); 

    if (parsedSettingsOverrides?.fps) {
        log.debug(`Using FPS from settings overrides: ${parsedSettingsOverrides.fps}`)
        return parsedSettingsOverrides.fps;
    } else if (activePreset?.clientSafeSettings.fps) {
        log.debug(`Using FPS from preset: ${activePreset?.clientSafeSettings.fps}`)
        return activePreset.clientSafeSettings.fps;
    } else {
        log.debug(`FPS unknown, defaulting to 20 for client-side operations.`)
        return 20;
    } 
});


export const requiredAudioFieldsAtom = atom((get) => {

    const parsedSettingsOverrides = get(parsedSettingsOverridesAtom); 
    const activePreset = get(activePresetInfoAtom);

    const overrideRequiredAudioFields = parsedSettingsOverrides ? detectRequiredAudioFields(parsedSettingsOverrides) : [];
    const requiredAudioFields = (activePreset?.clientSafeSettings.requiredAudioFields ?? []).concat(overrideRequiredAudioFields);

    log.debug("Required fields: ", {requiredAudioFields});
    return requiredAudioFields;

})

